import { useState } from 'react';
import { storyblokEditable } from '@storyblok/react';
import { StoryblokRichtext, render, MARK_LINK, NODE_PARAGRAPH, NODE_IMAGE } from 'storyblok-rich-text-react-renderer';
import Divider from '@mui/material/Divider';
import { StoryblokAsset } from '../../types/StoryblokAsset';

const accordionRichTextOptions = {
  markResolvers: {
    [MARK_LINK]: (children: React.ReactNode, props: any) => (
      <a
        href={props.href}
        target='_blank'
        rel='noopener noreferrer'
        className='text-flieder [&_*]:!text-flieder underline decoration-black hover:text-black hover:[&_*]:!text-black hover:decoration-flieder transition-colors duration-200'
      >
        {children}
      </a>
    ),
  },
  nodeResolvers: {
    [NODE_PARAGRAPH]: (children: React.ReactNode) => (
      <p className='mb-4 last:mb-0'>{children}</p>
    ),
    [NODE_IMAGE]: (_children: React.ReactNode, props: any) => (
      <span className='block my-6'>
        <img src={props.src} alt={props.alt || ''} title={props.title} className='max-w-full h-auto' />
      </span>
    ),
  },
};

type ContentBlock = {
  _uid: string;
  headline: string;
  paragraph: string;
  image?: StoryblokAsset;
  imagePosition?: 'left' | 'right';
  _editable?: string;
  component?: string;
};

type ServiceItem = {
  _uid: string;
  title: string;
  content?: StoryblokRichtext;
  _editable?: string;
  component?: string;
};

type ECommerceDetailBlok = {
  title?: string;
  subtitle?: string;
  heroImage?: StoryblokAsset;
  contentBlocks?: ContentBlock[];
  servicesHeadline?: string;
  services?: ServiceItem[];
  _editable?: string;
  component?: string;
};

type ECommerceDetailProps = {
  blok: ECommerceDetailBlok;
};

const hasRichtextContent = (content?: StoryblokRichtext) =>
  !!content?.content?.some((node: any) => node.content?.length);

const ServiceAccordionItem = ({
  title,
  content,
  isLast,
}: {
  title: string;
  content?: StoryblokRichtext;
  isLast?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasContent = hasRichtextContent(content);

  return (
    <div>
      <Divider
        sx={{
          borderBottomWidth: '3px',
          borderColor: isOpen ? '#000000' : '#A994FF',
        }}
      />
      <button
        onClick={() => hasContent && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between py-5 tablet:py-[30px] desktop:py-[40px] text-left group overflow-hidden ${!hasContent ? 'cursor-default' : ''}`}
        aria-expanded={isOpen}
      >
        <span
          className={`font-soehne text-[clamp(2.5rem,6vw,5.375rem)] leading-[0.8] lowercase transition-colors duration-200 ${isOpen ? 'text-flieder' : 'text-black'}`}
        >
          {title}
        </span>
        {hasContent && (
          <span
            className={`font-soehne text-[2.5rem] tablet:text-[4rem] leading-none ml-4 flex-shrink-0 self-center transition-colors duration-200 ${isOpen ? 'text-black' : 'text-flieder'}`}
          >
            {isOpen ? '×' : '+'}
          </span>
        )}
      </button>
      {isOpen && hasContent && (
        <>
          <Divider sx={{ borderBottomWidth: '3px', borderColor: '#000000' }} />
          <div className='py-5 tablet:py-[30px] desktop:py-[40px] font-body text-copy-md text-black leading-relaxed richtext'>
            {render(content, accordionRichTextOptions)}
          </div>
        </>
      )}
      {isLast && !isOpen && (
        <Divider sx={{ borderBottomWidth: '3px', borderColor: '#A994FF' }} />
      )}
    </div>
  );
};

const ContentBlockItem = ({
  block,
  index,
}: {
  block: ContentBlock;
  index: number;
}) => {
  const imageLeft =
    block.imagePosition === 'left' || (!block.imagePosition && index % 2 === 0);

  const imageContent = (
    <div
      className='relative w-full min-h-[300px] min-[1024px]:min-h-0 min-[1024px]:self-stretch overflow-hidden order-first min-[1024px]:order-none min-[1024px]:w-[310px] min-[1024px]:flex-none'
    >
      {block.image?.filename && (
        <img
          src={block.image.filename}
          alt={block.image.alt || block.headline}
          className='absolute inset-0 w-full h-full object-cover object-center'
        />
      )}
    </div>
  );

  const textContent = (
    <div
      className='w-full min-[1024px]:w-[534px] min-[1024px]:flex-none flex flex-col justify-center px-5 py-6 min-[1024px]:pl-[37px] min-[1024px]:pr-[53px] min-[1024px]:py-[37px] bg-flieder/65'
    >
      <Divider className='!bg-black' />
      <p className='font-soehne-schmal text-block-headline uppercase text-white py-3 leading-[100%]'> 
        {block.headline}
      </p>
      <Divider className='!bg-black mb-6' />
      <p className='font-body text-copy-md leading-[138%] text-black mt-4'>
        {block.paragraph}
      </p>
    </div>
  );

  return (
    <div
      {...storyblokEditable(block)}
      className='flex flex-col min-[1024px]:flex-row min-[1024px]:min-h-[404px]'
    >
      {imageLeft ? (
        <>
          {imageContent}
          {textContent}
        </>
      ) : (
        <>
          {textContent}
          {imageContent}
        </>
      )}
    </div>
  );
};

const ECommerceDetail = ({ blok }: ECommerceDetailProps) => {
  return (
    <article {...storyblokEditable(blok)}>
      {/* Hero */}
      <section className='relative w-full h-[420px] tablet:h-[520px] overflow-hidden'>
        {blok.heroImage?.filename && (
          <img
            src={blok.heroImage.filename}
            alt={blok.heroImage.alt || ''}
            className='absolute inset-0 w-full h-full object-cover'
          />
        )}
      </section>

      {/* Main content wrapper with flieder background tint */}
      <div>
        {/* Page title */}
        <div className='container mx-auto max-w-container px-5 tablet:px-10 desktop:px-20 pt-12 tablet:pt-16 pb-8 text-center'>
          <h1 className='font-soehne-schmal text-hero-page-title uppercase text-black text-center'>
            {blok.title || 'E-COMMERCE SOLUTIONS'}
          </h1>
          {blok.subtitle && (
            <h2 className='mx-auto max-w-full min-[1024px]:max-w-[843px] px-5 tablet:px-10 min-[1024px]:px-0 font-respira text-heading-lg text-black mt-4'>
              {blok.subtitle}
            </h2>
          )}
        </div>

        {/* Content blocks */}
        {blok.contentBlocks && blok.contentBlocks.length > 0 && (
          <div className='mx-auto max-w-full min-[1024px]:max-w-[843px] px-5 tablet:px-10 min-[1024px]:px-0 flex flex-col gap-[30px]'>
            {blok.contentBlocks.map((block, index) => (
              <ContentBlockItem key={block._uid} block={block} index={index} />
            ))}
          </div>
        )}

        {/* Services accordion */}
        <section className='mx-auto max-w-full min-[1024px]:max-w-[843px] px-5 tablet:px-10 min-[1024px]:px-0 pt-[40px] min-[1024px]:pt-[160px] pb-[40px] min-[1024px]:pb-[224px]'>
          {blok.servicesHeadline && (
            <p className='font-soehne-schmal text-section-label uppercase text-black mb-6'>
              {blok.servicesHeadline}
            </p>
          )}
          <div className='flex flex-col'>
            {blok.services?.map((item, index) => (
              <ServiceAccordionItem
                key={item._uid}
                title={item.title}
                content={item.content}
                isLast={index === (blok.services?.length ?? 0) - 1}
              />
            ))}
          </div>
        </section>
      </div>
    </article>
  );
};

export default ECommerceDetail;
